// ============================================================
// TERROR — COSMETIC MANIFEST
// Maps app-agnostic cosmetic IDs (from IE_COSMETIC_CATALOG.md)
// to Terror's sprite assets.
//
// Shape MUST match Pier's and Snake Eyes' manifests — same keys,
// different URLs. Each game renders the same cosmetic in its own
// visual language.
//
// Missing-render fallback: getCosmeticAsset returns null if Terror
// doesn't have a sprite for an ID. Never throws, never errors —
// renderers skip the slot silently.
//
// Add a cosmetic:
//   1. Add the entry to IE_COSMETIC_CATALOG.md first (canonical)
//   2. Add a row in the matching slot below with the asset URL
//   3. Drop the .webp into terror-assets.pages.dev/cosmetics/
//
// Naming convention for assets:
//   terror-assets.pages.dev/cosmetics/{slot}_{id}.webp
//
// Until Midjourney art lands, entries 404 gracefully through
// <Sprite>'s onError fallback. Safe to ship empty.
// ============================================================

import { CDN } from "./CDN";

// ── ASSET URL HELPER ──────────────────────────────────────────
const cosmeticUrl = (filename) => `${CDN}/cosmetics/${filename}`;

// ── THE MANIFEST ──────────────────────────────────────────────
// All 61 seeded catalog entries. URLs resolve to 404 until assets
// are dropped into the CDN folder — Sprite fallback handles that.

export const COSMETICS = {
  head: {
    aenarchist_crown:       cosmeticUrl("head_aenarchist_crown.webp"),
    bebling_beanie:         cosmeticUrl("head_bebling_beanie.webp"),
    croupiers_visor:        cosmeticUrl("head_croupiers_visor.webp"),
    dock_knit_cap:          cosmeticUrl("head_dock_knit_cap.webp"),
    gamblers_top_hat:       cosmeticUrl("head_gamblers_top_hat.webp"),
    harbormasters_tricorne: cosmeticUrl("head_harbormasters_tricorne.webp"),
    jynxi_jesters_hood:     cosmeticUrl("head_jynxi_jesters_hood.webp"),
    lyt_brow_circlet:       cosmeticUrl("head_lyt_brow_circlet.webp"),
    nihil_shroud:           cosmeticUrl("head_nihil_shroud.webp"),
    omnis_coronet:          cosmeticUrl("head_omnis_coronet.webp"),
    ruks_broken_helm:       cosmeticUrl("head_ruks_broken_helm.webp"),
    scholars_cowl:          cosmeticUrl("head_scholars_cowl.webp"),
    willies_old_cap:        cosmeticUrl("head_willies_old_cap.webp"),
  },

  face: {
    card_sharp_domino:      cosmeticUrl("face_card_sharp_domino.webp"),
    khao_duhmdumn_paint:    cosmeticUrl("face_khao_duhmdumn_paint.webp"),
    dragons_breath_mask:    cosmeticUrl("face_dragons_breath_mask.webp"),
    fog_veil:               cosmeticUrl("face_fog_veil.webp"),
    h01_visor:              cosmeticUrl("face_h01_visor.webp"),
    jynxi_trickster_mask:   cosmeticUrl("face_jynxi_trickster_mask.webp"),
    salt_spray_goggles:     cosmeticUrl("face_salt_spray_goggles.webp"),
    smoke_mask:             cosmeticUrl("face_smoke_mask.webp"),
    soulcerers_blindfold:   cosmeticUrl("face_soulcerers_blindfold.webp"),
    spectral_tiger_stripes: cosmeticUrl("face_spectral_tiger_stripes.webp"),
  },

  body: {
    anarchists_cloak:       cosmeticUrl("body_anarchists_cloak.webp"),
    apprentices_robe:       cosmeticUrl("body_apprentices_robe.webp"),
    dealers_vest:           cosmeticUrl("body_dealers_vest.webp"),
    dockhands_coat:         cosmeticUrl("body_dockhands_coat.webp"),
    empire_regalia:         cosmeticUrl("body_empire_regalia.webp"),
    gatekeepers_plate:      cosmeticUrl("body_gatekeepers_plate.webp"),
    gilded_gamblers_suit:   cosmeticUrl("body_gilded_gamblers_suit.webp"),
    ink_robe:               cosmeticUrl("body_ink_robe.webp"),
    lighthouse_keepers_coat: cosmeticUrl("body_lighthouse_keepers_coat.webp"),
    omnia_vestments:        cosmeticUrl("body_omnia_vestments.webp"),
    pinstripe_suit:         cosmeticUrl("body_pinstripe_suit.webp"),
    pirate_wrap:            cosmeticUrl("body_pirate_wrap.webp"),
    starter_tunic:          cosmeticUrl("body_starter_tunic.webp"),
  },

  back: {
    companion_harness:      cosmeticUrl("back_companion_harness.webp"),
    dealers_apron:          cosmeticUrl("back_dealers_apron.webp"),
    gambler_cape:           cosmeticUrl("back_gambler_cape.webp"),
    harbormaster_longcoat:  cosmeticUrl("back_harbormaster_longcoat.webp"),
    nullus_cape:            cosmeticUrl("back_nullus_cape.webp"),
    omnis_cloak:            cosmeticUrl("back_omnis_cloak.webp"),
    phantom_drift:          cosmeticUrl("back_phantom_drift.webp"),
    shadow_wings:           cosmeticUrl("back_shadow_wings.webp"),
    shipwreck_kit:          cosmeticUrl("back_shipwreck_kit.webp"),
    snake_eyes_banner:      cosmeticUrl("back_snake_eyes_banner.webp"),
    spectral_tail:          cosmeticUrl("back_spectral_tail.webp"),
  },

  aura: {
    crest_well_shimmer:     cosmeticUrl("aura_crest_well_shimmer.webp"),
    first_win_glow:         cosmeticUrl("aura_first_win_glow.webp"),
    gold_leaf_sheen:        cosmeticUrl("aura_gold_leaf_sheen.webp"),
    ink_drift:              cosmeticUrl("aura_ink_drift.webp"),
    lantern_glow:           cosmeticUrl("aura_lantern_glow.webp"),
    neon_halo:              cosmeticUrl("aura_neon_halo.webp"),
    prophecy_flicker:       cosmeticUrl("aura_prophecy_flicker.webp"),
    ruk_emberfall:          cosmeticUrl("aura_ruk_emberfall.webp"),
    saltwater_aura:         cosmeticUrl("aura_saltwater_aura.webp"),
    // ⭐ THE PUPIL'S PRIZE, 2026-08-22. caP'n: *"the middle pupil will
    // give you a Mythical SHV Backdrop and Coin and Lux/Grim aura."*
    // Granted at ASS-50 and nowhere else. Like every other row here it
    // is one line and one filename, and it 404s gracefully until the
    // art lands — which is the whole reason this manifest is a table of
    // URLs rather than a table of drawings.
    shv_sear:               cosmeticUrl("aura_shv_sear.webp"),
  },

  title: {
    // Titles render as text, not sprites. The string is the display text.
    aenarchist:    "Aenarchist",
    apprentice:    "Apprentice",
    bladesworn:    "Bladesworn",
    crest_trader:  "Crest Trader",
    dragon_slayer: "Dragon Slayer",
    harbormaster:  "Harbormaster",
    high_roller:   "High Roller",
    hollowsworn:   "Hollowsworn",
    house_killer:  "House Killer",
    jynxis_jester: "J'ynxi's Jester",
    lucky_one:     "the Lucky One",
    the_unbroken:  "the Unbroken",
    the_wanderer:  "the Wanderer",
    welcomed:      "the Welcomed",

    // ── H9 · THE TITLES PIPELINE ──────────────────────────────
    // One row per family in titles.js, keyed `title_<familyId>`.
    // THE STRING LIVES HERE, the family lives there, and the two are
    // joined by cosmeticIdFor() — so a title's DISPLAY is a manifest
    // edit and its EARNING is a registry edit, and neither ever needs
    // the other's file open. auditTitleManifest() below proves the
    // two tables still agree.
    // ⚠ V'ael's placeholder prose. caP'n's canon veto stands.
    title_first_light: "the Newly Woken",
    title_collector:   "Keeper of the Whole Codex",
    title_foilwright:  "the Foilwright",
    title_descent:     "Aenarchist of the Endless Depths",
    title_sealing:     "the One Who Closed the Seal",
    title_solrender:   "the Ranked",
    title_freehand:    "the Unhurried",
    title_menagerie:   "the Well-Loved",
    title_atelier:     "the Well-Dressed",
    // ⭐ "the Fourfold". THIS TABLE IS WHAT THE PLAYER ACTUALLY WEARS —
    // TheMark renders from COSMETICS.title, not from titles.js — so the
    // 2026-08-20 rename fixed the count in achievements.js,
    // progression.js, titles.js and storyData.js and missed the one
    // table that drives the pixels. A player who completed all four
    // Affinities was congratulated with "the Fourfold" and then wore
    // "the Fivefold" on their nameplate forever.
    title_affinities:  "the Fourfold",
    title_pier_walk:   "the Wanderer",
    title_house:       "the High Roller",
    title_sol_party:   "the Assembled",
    title_perry_chain: "the Unbroken Rhythm",
    title_perry_road:  "the Long Walker",
    title_perry_ascetic: "the One Who Took Nothing",
    title_the_fold:    "the Tenfold",
    title_citizen:     "Citizen of the Illegal Empire",
    title_unwritten:   "the Unwritten",

    // ── H5 · FACTION WARS — the week's spoils ─────────────────
    // NOT a titles.js family: a family is a lifetime arc and these are
    // won in seven days by whoever showed up. They live in the same
    // slot and equip the same way; only their earn path differs.
    // titles.js's auditTitleStrings() skips the `title_war_` prefix for
    // exactly this reason, and says so where it does it.
    title_war_nullus:  "Sword of the Winning Week",
    title_war_omnis:   "Blade of the Winning Week",
    title_war_jesters: "Jester of the Winning Week",
  },
};

// ── LOOKUP HELPERS ────────────────────────────────────────────

// Get the renderable asset for an equipped cosmetic. Returns null if
// Terror doesn't have a sprite/text for it (safe — caller skips the slot).
export const getCosmeticAsset = (slot, cosmeticId) => {
  if (!slot || !cosmeticId) return null;
  return COSMETICS[slot]?.[cosmeticId] ?? null;
};

// Batch version — takes an `equipped` object (from listenToEquipped) and
// returns an object with the same 6 keys but values resolved to asset
// URLs (for sprite slots) / display text (for title) / null if missing.
export const resolveEquipped = (equipped) => {
  if (!equipped) return {};
  const out = {};
  for (const [slot, id] of Object.entries(equipped)) {
    out[slot] = getCosmeticAsset(slot, id);
  }
  return out;
};

// Does Terror have a render for this cosmetic?
// (Useful for Wardrobe preview — grey out items this app can't show.)
export const canRender = (slot, cosmeticId) =>
  getCosmeticAsset(slot, cosmeticId) !== null;

// The H9 two-table audit lives in titles.js (auditTitleStrings) — it
// belongs to the pipeline that owns the families, and keeping it there
// leaves this manifest with ZERO dependency on the achievement layer.
// A surface that only wants a hat must never have to load progression.
